import { ParseKeysByKeyPrefix } from 'i18next'
import { CheckCircle2, Clock, FileText, Package, Plane, Truck } from 'lucide-react'
import { useMemo } from 'react'
import { Trans, useTranslation } from 'react-i18next'

import { useParcelStatus } from '@/api/shipping/queries/useParcelStatus'
import { AppDialog } from '@/components/AppDialog'
import { Button } from '@/components/ui/button'
import { Spinner } from '@/components/ui/spinner'
import { formatDateEu } from '@/helpers/formatDate'
import { cn } from '@/lib/utils'

interface Props {
  toTrackingNumber: string
  isOpen: boolean
  onOpenChange: (open: boolean) => void
}

// Map status codes to icons (text comes from translations)
const STATUS_ICONS: Record<string, typeof CheckCircle2> = {
  '00': Clock,
  '10': Package,
  '15': Plane,
  '20': Truck,
  '21': Package,
  '29': Truck,
  '32': FileText,
  '36': Package,
  '42': FileText,
  '43': Package,
  '44': Package,
  '45': Plane,
  '48': Package,
  '50': Truck,
  '51': CheckCircle2,
  '52': CheckCircle2,
  '53': Clock,
  '60': Truck,
}

const formatDate = (dateString: string) => formatDateEu(dateString)

const formatShippingDate = (dateString: string) => formatDateEu(dateString)

export const DeliveryStatus = ({ toTrackingNumber, isOpen, onOpenChange }: Props) => {
  const { t, i18n } = useTranslation()

  const { data: statusData, isLoading, error } = useParcelStatus(toTrackingNumber, isOpen)

  // Statuses come in reverse chronological order (newest first), so reverse them
  const sortedStatuses = useMemo(() => {
    if (!statusData?.statuses) return []
    // Reverse to show chronological order (oldest first)
    return [...statusData.statuses].reverse()
  }, [statusData])

  // Find the current/active status (usually the latest non-delivered status)
  const currentStatusIndex = useMemo(() => {
    if (!sortedStatuses || sortedStatuses.length === 0) return -1

    // Find the delivered status (code 51 or 52)
    const deliveredIndex = sortedStatuses.findIndex(s => s.code === '51' || s.code === '52')

    // If delivered, that's the current one (highlighted)
    if (deliveredIndex !== -1) return deliveredIndex

    // If no delivered status, the last one (most recent) is current
    return sortedStatuses.length - 1
  }, [sortedStatuses])

  // Get shipping date from the earliest status or package
  const shippingDate = useMemo(() => {
    if (sortedStatuses && sortedStatuses.length > 0) {
      // The first status in sorted array is the earliest
      const earliestStatus = sortedStatuses[0]
      return formatShippingDate(earliestStatus.dateTime)
    }
  }, [sortedStatuses])

  return (
    <AppDialog isOpen={isOpen} onOpenChange={onOpenChange}>
      <div className='flex flex-col gap-4 pt-4'>
        {isLoading && (
          <div className='flex flex-col items-center justify-center gap-4 py-8'>
            <p className='text-text-secondary'>{t('pkg.deliveryStatusLoading')}</p>
            <Spinner className='size-10' />
          </div>
        )}

        {!!error && (
          <div className='flex flex-col items-center justify-center gap-4 py-8'>
            <p className='text-text-secondary text-center'>{t('pkg.deliveryStatusError')}</p>
            <Button variant='outline' onClick={() => onOpenChange(false)}>
              {t('common.close')}
            </Button>
          </div>
        )}

        {!isLoading && !error && statusData && (
          <>
            {/* Shipping Info */}
            <div className='flex flex-col gap-1'>
              <p className='text-text-primary text-base font-medium'>{t('pkg.deliveryStatusTitle')}</p>
              <p className='text-text-secondary text-xs'>
                {t('pkg.deliveryStatusShippedOn', {
                  date: shippingDate,
                  trackingNumber: statusData.parcel.trackingNumber,
                })}
              </p>
            </div>

            {/* Status Timeline */}
            <div className='flex max-h-[400px] flex-col gap-[27px] overflow-y-auto px-2'>
              {sortedStatuses.map((status, index) => {
                const isActive = index === currentStatusIndex
                const isPast = index < currentStatusIndex
                const translationKey = `pkg.deliveryStatusCodes.${status.code}` as ParseKeysByKeyPrefix<'pkg', 'deliveryStatusCodes'>
                const hasTranslation = i18n.exists(translationKey)
                const Icon = STATUS_ICONS[status.code] ?? Clock
                const statusDate = formatDate(status.dateTime)

                return (
                  <div
                    key={`${status.code}-${status.dateTime}`}
                    className={cn('flex items-start justify-end gap-1', isActive && 'bg-button-primary-selected-segment rounded-xl p-2')}
                  >
                    <div className='flex grow flex-col gap-1'>
                      <p className={cn('text-sm', isActive ? 'text-text-primary' : 'text-text-disabled', isPast && 'text-text-secondary')}>
                        {hasTranslation ? <Trans i18nKey={translationKey} /> : status.status}
                      </p>
                      <p className={cn('text-xs', isActive ? 'text-text-secondary' : 'text-text-disabled', isPast && 'text-text-disabled')}>
                        {statusDate}
                      </p>
                    </div>
                    <Icon
                      className={cn('size-5', isActive ? 'text-text-primary' : 'text-text-disabled', isPast && 'text-text-secondary')}
                    />
                  </div>
                )
              })}
            </div>

            {/* Delivery Link */}
            <a
              href={`https://myparcel.exelot.com/tracking?barcodes=${statusData.parcel.trackingNumber}`}
              target='_blank'
              rel='noopener noreferrer'
            >
              <Button variant='link' className='text-text-link hover:text-text-link/80 text-sm underline underline-offset-2'>
                {t('pkg.deliveryStatusFullDetails')}
              </Button>
            </a>
          </>
        )}
      </div>
    </AppDialog>
  )
}
